"use client";
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';

export default function LiveAccessGate({ children, authenticated = false, expiresAt, unavailable = false }: {
  children?: React.ReactNode; authenticated?: boolean; expiresAt?: number; unavailable?: boolean;
}) {
  const router = useRouter();
  const [active, setActive] = useState(authenticated);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setActive(authenticated); }, [authenticated, expiresAt]);
  useEffect(() => {
    if (!active) return;
    let disposed = false;
    const controller = new AbortController();
    const clear = () => {
      if (disposed) return;
      setActive(false); // unmount all dashboard hooks and their sensitive state
      try { for (const key of Object.keys(localStorage)) if (key.startsWith('nas-cache:') || key === 'rag-exp-chat-history' || key === 'rag-chat-history') localStorage.removeItem(key); } catch {}
      router.refresh();
    };
    const check = async () => {
      try { const response = await fetch('/api/live/session', { cache: 'no-store', signal: controller.signal }); if (!response.ok) clear(); }
      catch { if (!disposed) clear(); }
    };
    const interval = setInterval(check, 15000);
    const expiry = setTimeout(clear, Math.max(0, (expiresAt || 0) - Date.now()));
    const onUnauthorized = () => clear();
    window.addEventListener('live-session-expired', onUnauthorized);
    return () => { disposed = true; controller.abort(); clearInterval(interval); clearTimeout(expiry); window.removeEventListener('live-session-expired', onUnauthorized); };
  }, [active, expiresAt, router]);
  const login = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const response = await fetch('/api/live/login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }) });
      setPassword('');
      if (!response.ok) { setError(response.status === 503 ? '인증 서버 설정을 확인해야 합니다.' : '로그인 실패'); return; }
      router.refresh();
    } catch { setPassword(''); setError('연결 실패'); } finally { setBusy(false); }
  };
  const logout = async () => {
    setBusy(true);
    try {
      const response = await fetch('/api/live/logout', { method: 'POST' });
      if (!response.ok && response.status !== 401) { setError('로그아웃 실패'); return; }
      window.dispatchEvent(new Event('live-session-expired')); setActive(false); router.refresh();
    } catch { setError('로그아웃 연결 실패'); } finally { setBusy(false); }
  };
  if (!active) return <main><h1>SnapAgent LIVE 로그인</h1><form onSubmit={login}><label>접근 암호<input type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} maxLength={256} /></label><button disabled={busy || unavailable}>로그인</button></form><p role="alert">{unavailable ? '인증 서버 설정을 확인해야 합니다.' : error}</p></main>;
  return <><button onClick={logout} disabled={busy}>로그아웃</button><p role="alert">{error}</p>{children}</>;
}
